import { Alert, Button, Flex, Stack } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { InputFormControl, SwitchFormControl } from '../components'
import { BadRequestError, type LoginRequest, type LoginResponse, type ModelState } from '../dtos'
import { useReturnUrlNavigate } from '../hooks'
import { setModelStateErrors } from '../Utils/formModelState'
import { loginFormSchema } from '../validation/loginFormSchema'

export interface LoginFormProps {
  returnUrl?: string
  error?: boolean
  forgotPasswordUrl?: string
  login: (data: LoginRequest) => Promise<LoginResponse>
}

export const LoginForm = ({ returnUrl: returnUrlParams, forgotPasswordUrl, login }: LoginFormProps) => {
  const methods = useForm<LoginRequest>({
    resolver: yupResolver(loginFormSchema) as any,
  })
  const { handleSubmit, formState: { isSubmitting, errors }, setError, control } = methods

  const returnUrlNavigate = useReturnUrlNavigate()
  const onSubmit = async (values: LoginRequest) => {
    try {
      const response = await login(values)

      const returnUrl = response?.returnUrl ?? returnUrlParams ?? '/'

      returnUrlNavigate(returnUrl)
    } catch (e: any) {
      // Handle 400 Bad Request errors (validation errors from server)
      if (e?.response?.status === 400 && e?.response?.data) {
        setModelStateErrors(setError, new BadRequestError(e.response.data as ModelState<LoginRequest>))
      }
      // Handle 401 Unauthorized errors
      else if (e?.response?.status === 401) {
        setError('root', {
          type: 'manual',
          message: 'Nesprávné uživatelské jméno nebo heslo'
        })
      }
      // Handle network errors
      else if (!e?.response) {
        setError('root', {
          type: 'manual',
          message: 'Chyba připojení k serveru. Zkuste to prosím později.'
        })
      }
      // Handle other errors
      else {
        setError('root', {
          type: 'manual',
          message: 'Došlo k neočekávané chybě. Zkuste to prosím znovu.'
        })
      }
    }
  }

  return (<>
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, (e) => console.log(e))}>
        <Stack sx={{ gap: 6, justifyItems: 'center', w: 'full', minW: 'xs' }}>
          <InputFormControl name='userName' control={control}
            label=''
            placeholder='Uživatelské jméno'
            autoComplete="username"
            formControlVariant="fullWidth"
            isRequired
            tabIndex={1} />

          <InputFormControl name='password' control={control}
            labelProps={{ w: 'full' }}
            label=''
            formControlVariant="fullWidth"
            type="password"
            placeholder="Heslo"
            autoComplete="password"
            isRequired
            tabIndex={2} />

          <Flex
            sx={{ gap: 16, justifyContent: 'space-between', flexWrap: 'wrap' }}
          >
            <SwitchFormControl name='rememberMe' control={control} formControlVariant="inline" label="Zapamatovat" size="sm" tabIndex={4} />

            {forgotPasswordUrl && (
              <Button variant="link" as={Link} to={forgotPasswordUrl}>
                Zapomenuté heslo?
              </Button>
            )}
          </Flex>

          <Button
            bg='login-button-bg'
            _hover={{ bg: 'login-button-bg-hover' }}
            type="submit"
            isLoading={isSubmitting}
            mt={4}
            size="lg"
            w={{ base: 'full', md: 'auto' }}
          >
            Přihlásit se
          </Button>
          {errors.root && <Alert variant={'solid'} status='error'>{errors.root.message}</Alert>}
        </Stack>
      </form>
    </FormProvider>
  </>
  )
}